import { FC, ReactElement, ReactNode, useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  InputAdornment,
  SxProps,
  TextField,
  Theme,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { EMAIL_REGEX } from '@constants/constants';

const DEFAULT_MULTI_EMAIL_CHIP_SX: SxProps<Theme> = {
  backgroundColor: '#2F736E1F',
  border: '1px solid #AECBD0',
  color: '#162936',
  fontWeight: 500,
  fontSize: '0.875rem',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  borderRadius: '0.5rem',
};

const DEFAULT_MULTI_EMAIL_TEXT_FIELD_SX: SxProps<Theme> = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '0.5rem',
    '& fieldset': {
      borderColor: '#737F86',
    },
    '&:hover fieldset': {
      borderColor: '#737F86',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#737F86 !important',
      borderWidth: '1px !important',
    },
  },
};

const normalizeEmails = (emails: string[]) => {
  const validEmails = emails
    .filter((email) => EMAIL_REGEX.test(email.trim()))
    .map((email) => email.trim());
  return [...new Set(validEmails)];
};

export interface MultiEmailChipsInputProps {
  value: string[];
  onChange: (emails: string[]) => void;
  /** When provided and false, clears the free-text input and focus state */
  open?: boolean;
  placeholder?: string;
  disabled?: boolean;
  showError?: boolean;
  onTouched?: () => void;
  onClearError?: () => void;
  label?: string;
  labelSx?: SxProps<Theme>;
  chipSx?: SxProps<Theme>;
  textFieldSx?: SxProps<Theme>;
  autocompleteSx?: SxProps<Theme>;
  containerSx?: SxProps<Theme>;
  startAdornment?: ReactNode | null;
  deleteIcon?: ReactElement;
}

const MultiEmailChipsInput: FC<MultiEmailChipsInputProps> = ({
  value,
  onChange,
  open,
  placeholder = 'Enter comma-separated emails',
  disabled = false,
  showError = false,
  onTouched,
  onClearError,
  label,
  labelSx,
  chipSx = DEFAULT_MULTI_EMAIL_CHIP_SX,
  textFieldSx = DEFAULT_MULTI_EMAIL_TEXT_FIELD_SX,
  autocompleteSx = { width: '100%' },
  containerSx,
  startAdornment,
  deleteIcon = <CloseIcon sx={{ fontSize: '1rem', color: '#162C36 !important' }} />,
}) => {
  const [emailInputValue, setEmailInputValue] = useState('');
  const [emailInputFocused, setEmailInputFocused] = useState(false);

  useEffect(() => {
    if (open === false) {
      setEmailInputValue('');
      setEmailInputFocused(false);
    }
  }, [open]);

  const handleEmailsChange = (
    _event: React.SyntheticEvent<Element, Event> | null,
    newValue: string[],
  ) => {
    const uniqueEmails = normalizeEmails(newValue);
    onChange(uniqueEmails);
    setEmailInputValue('');
    if (uniqueEmails.length > 0) {
      onClearError?.();
    }
  };

  const tryAddEmailFromInput = (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) return;
    if (EMAIL_REGEX.test(trimmed) && !value.includes(trimmed)) {
      handleEmailsChange(null, [...value, trimmed]);
    }
  };

  const resolvedStartAdornment =
    startAdornment === null
      ? null
      : (startAdornment ?? <EmailOutlinedIcon sx={{ fontSize: '1.5rem', color: '#3D4C56' }} />);

  const field = (
    <Autocomplete
      multiple
      freeSolo
      disableClearable
      disabled={disabled}
      options={[]}
      value={value}
      inputValue={emailInputValue}
      onChange={(event, newValue) => handleEmailsChange(event, newValue)}
      onInputChange={(_event, newInputValue) => {
        setEmailInputValue(newInputValue);
      }}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            variant="outlined"
            label={option}
            {...getTagProps({ index })}
            key={option}
            deleteIcon={deleteIcon}
            sx={chipSx}
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={value.length === 0 ? placeholder : ''}
          InputProps={{
            ...params.InputProps,
            startAdornment: resolvedStartAdornment ? (
              <>
                <InputAdornment position="start">{resolvedStartAdornment}</InputAdornment>
                {params.InputProps.startAdornment}
              </>
            ) : (
              params.InputProps.startAdornment
            ),
          }}
          sx={textFieldSx}
          onFocus={() => setEmailInputFocused(true)}
          onBlur={() => {
            setEmailInputFocused(false);
            onTouched?.();
            tryAddEmailFromInput(emailInputValue);
          }}
          helperText={
            emailInputValue.trim() &&
            !emailInputFocused &&
            !EMAIL_REGEX.test(emailInputValue.trim())
              ? `Invalid email format: '${emailInputValue.trim()}'`
              : undefined
          }
          error={showError}
          onKeyDown={(e) => {
            if (e.key === ',' || e.key === ' ' || e.key === 'Enter') {
              e.preventDefault();
              tryAddEmailFromInput(emailInputValue);
            }
          }}
        />
      )}
      sx={autocompleteSx}
    />
  );

  if (!label) {
    return <Box sx={containerSx}>{field}</Box>;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        gap: '0.75rem',
        flexWrap: 'wrap',
        ...containerSx,
      }}
    >
      <Typography
        sx={{
          fontSize: '1rem',
          fontWeight: 700,
          lineHeight: '1.375rem',
          color: '#162936',
          ...labelSx,
        }}
      >
        {label}
      </Typography>
      {field}
    </Box>
  );
};

export default MultiEmailChipsInput;
